import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Bell,
  Loader2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Info,
  ChevronDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  useNotifications,
  useUnreadNotificationsCount,
} from "@/features/notification/hooks/use-notifications";
import {
  useCoreNotifications,
  useCoreUnreadNotificationsCount,
} from "@/features/core-notification/hooks/use-core-notifications";
import { useNavigate } from "react-router-dom";
import { notificationApi } from "@/features/notification/services/notification-api";
import { coreNotificationApi } from "@/features/core-notification/services/core-notification-api";
import { formatDate } from "@/lib/ui-styles";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/features/auth/context/auth-context";
import { getSocket } from "@/lib/socket";
import {
  parseNotificationNavigateTarget,
  resolveTenantNotificationTarget,
} from "@/features/notification/lib/resolve-notification-target";
import type { Notification as TenantNotification } from "@/features/notification/types/notification.types";
import type { CoreNotification } from "@/features/core-notification/types/core-notification.types";

type GroupedNotification = {
  id: string;
  createdAt: string;
  readAt: string | null;
  href?: string | null;
  materialRequestId?: string | null;
  source: "core" | "tenant";
  isLoading: boolean;
  displayIcon: React.ReactNode;
  displayTitle: string;
  displayMessage: string;
  onMarkRead: () => void;
  onClick: () => void;
};

export const NotificationBell: React.FC = React.memo(() => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { selectedSubscriptionId } = useAuth();

  const isTenantMode = Boolean(
    selectedSubscriptionId && selectedSubscriptionId !== "SYSTEM",
  );

  const { data: tenantNotifications, mutate: mutateTenant } = useNotifications(
    { limit: 10 },
    isTenantMode,
  );
  const {
    notifications: coreNotifications,
    mutate: mutateCore,
    hasMore,
    loadMore,
  } = useCoreNotifications();
  const { count: tenantUnread } = useUnreadNotificationsCount(isTenantMode);
  const { count: coreUnread, mutate: mutateCoreUnread } =
    useCoreUnreadNotificationsCount();

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    const handler = () => {
      mutateCore();
      mutateCoreUnread();
    };
    socket.on("core-notification", handler);

    const readHandler = () => {
      mutateCoreUnread();
    };
    socket.on("core-notification-read", readHandler);

    return () => {
      socket.off("core-notification", handler);
      socket.off("core-notification-read", readHandler);
    };
  }, [mutateCore, mutateCoreUnread]);

  const totalUnread = tenantUnread + coreUnread;

  const mergeAndSort = useCallback(() => {
    const grouped: GroupedNotification[] = [];

    for (const n of tenantNotifications) {
      grouped.push({
        id: n.id,
        createdAt: n.createdAt,
        readAt: n.readAt ?? null,
        href: n.href,
        materialRequestId: n.materialRequestId,
        source: "tenant",
        isLoading: false,
        displayIcon: <Info className="h-4 w-4 text-primary" />,
        displayTitle: n.text,
        displayMessage: "",
        onMarkRead: () => handleMarkAsRead(n.id, "tenant"),
        onClick: () => handleTenantClick(n),
      });
    }

    for (const n of coreNotifications) {
      grouped.push({
        id: n.id,
        createdAt: n.createdAt,
        readAt: n.readAt ?? null,
        href: n.href,
        source: "core",
        isLoading: n.type === "loading",
        displayIcon: getCoreIcon(n.type),
        displayTitle: n.text,
        displayMessage: "",
        onMarkRead: () => handleMarkAsRead(n.id, "core"),
        onClick: () => handleCoreClick(n),
      });
    }

    grouped.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );

    return grouped;
  }, [tenantNotifications, coreNotifications]);

  const allNotifications = useMemo(mergeAndSort, [
    tenantNotifications,
    coreNotifications,
  ]);

  const markVisibleAsRead = useCallback(async () => {
    const coreIds: string[] = [];
    const tenantIds: string[] = [];

    for (const n of allNotifications) {
      if (n.readAt || n.isLoading) continue;
      if (n.source === "core") coreIds.push(n.id);
      else tenantIds.push(n.id);
    }

    try {
      if (coreIds.length > 0) {
        await Promise.all(
          coreIds.map((id) => coreNotificationApi.markAsRead(id)),
        );
        mutateCore();
        mutateCoreUnread();
      }
      if (tenantIds.length > 0) {
        await Promise.all(
          tenantIds.map((id) => notificationApi.markAsRead(id)),
        );
        if (mutateTenant) mutateTenant();
      }
    } catch (error) {
      console.error("Error marking visible notifications as read:", error);
    }
  }, [allNotifications, mutateCore, mutateCoreUnread, mutateTenant]);

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      setOpen(newOpen);
      if (newOpen) {
        markVisibleAsRead();
      }
    },
    [markVisibleAsRead],
  );

  const handleMarkAsRead = async (
    notificationId: string,
    source: "core" | "tenant",
  ) => {
    try {
      if (source === "core") {
        await coreNotificationApi.markAsRead(notificationId);
        mutateCore();
        mutateCoreUnread();
      } else {
        await notificationApi.markAsRead(notificationId);
        mutateTenant();
      }
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const navigateToNotificationTarget = useCallback(
    (target: string | null | undefined) => {
      const parsed = parseNotificationNavigateTarget(target);
      if (!parsed) return false;
      navigate({ pathname: parsed.pathname, search: parsed.search });
      setOpen(false);
      return true;
    },
    [navigate],
  );

  const handleTenantClick = async (notification: TenantNotification) => {
    if (!notification.readAt) {
      await handleMarkAsRead(notification.id, "tenant");
    }

    navigateToNotificationTarget(resolveTenantNotificationTarget(notification));
  };

  const handleCoreClick = async (notification: CoreNotification) => {
    if (!notification.readAt) {
      await handleMarkAsRead(notification.id, "core");
    }

    navigateToNotificationTarget(notification.href);
  };

  const handleMarkAllAsRead = async () => {
    try {
      const promises: Promise<{ count: number } | { message: string }>[] = [
        coreNotificationApi.markAllAsRead(),
      ];
      if (isTenantMode) {
        promises.push(notificationApi.markAllAsRead());
      }
      await Promise.all(promises);

      if (mutateTenant) mutateTenant();
      mutateCore();
      mutateCoreUnread();
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {totalUnread > 0 && (
            <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-destructive text-destructive-foreground text-xs">
              {totalUnread > 9 ? "9+" : totalUnread}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0 overflow-hidden" align="end">
        <div className="flex items-center justify-between p-3 border-b">
          <h3 className="font-semibold text-sm">Notificaciones</h3>
          {totalUnread > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMarkAllAsRead}
              className="text-xs"
            >
              Marcar todas como leídas
            </Button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {allNotifications.length === 0 ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              No hay notificaciones
            </div>
          ) : (
            <div className="divide-y">
              {allNotifications.map((item) => (
                <div
                  key={`${item.source}-${item.id}`}
                  className={`p-3 cursor-pointer bg-background`}
                  onClick={item.onClick}
                >
                  <div className="flex items-start gap-2">
                    <div className="mt-0.5 shrink-0">{item.displayIcon}</div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-xs break-words">
                        {item.displayTitle}
                      </div>
                      {item.displayMessage && (
                        <div className="text-xs text-muted-foreground mt-1 break-words">
                          {item.displayMessage}
                        </div>
                      )}
                      <div className="text-xs text-muted-foreground mt-1">
                        {formatDate(item.createdAt, "short")}
                      </div>
                    </div>
                    {!item.readAt && !item.isLoading && (
                      <div className="w-2 h-2 bg-primary rounded-full mt-1 shrink-0" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {hasMore && (
          <div className="p-2 border-t">
            <Button
              variant="ghost"
              size="sm"
              onClick={loadMore}
              className="w-full text-xs"
            >
              <ChevronDown className="h-3 w-3 mr-1" />
              Ver más
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
});

function getCoreIcon(type: string): React.ReactNode {
  switch (type) {
    case "loading":
      return <Loader2 className="h-4 w-4 text-primary animate-spin" />;
    case "success":
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    case "error":
      return <XCircle className="h-4 w-4 text-destructive" />;
    case "alert":
      return <AlertTriangle className="h-4 w-4 text-amber-500" />;
    default:
      return <Info className="h-4 w-4 text-primary" />;
  }
}
